// src/components/mascot/ChristmasDecor.js
// ─────────────────────────────────────────────────────────────
// Même esprit que HalloweenDecor.js (voir ce fichier pour le contexte) :
// une petite rafale de flocons de neige par intervalles, pas une neige
// continue — même discipline de performance (animation finie, DOM
// nettoyé, intervalle largement espacé).
// ─────────────────────────────────────────────────────────────

import React, { useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { isChristmasPeriod } from "./seasonal";

const FLURRY_INTERVAL_MS = 40000; // une petite rafale de flocons toutes les ~40s
const FLAKES_PER_FLURRY = 6;

const KEYFRAMES = `
@keyframes christmas-snow-fall {
  0% { transform: translate(0, -5vh) rotate(0deg); opacity: 0; }
  10% { opacity: 0.9; }
  90% { opacity: 0.9; }
  100% { transform: translate(var(--drift), 105vh) rotate(180deg); opacity: 0; }
}
`;

function Snowflake({ leftVw, duration, size, drift }) {
  return (
    <Box
      sx={{
        position: "fixed", top: 0, left: `${leftVw}vw`, zIndex: 1250, pointerEvents: "none",
        "--drift": `${drift}px`,
        animation: `christmas-snow-fall ${duration}s linear forwards`,
        fontSize: size, color: "#dbeafe", textShadow: "0 0 4px rgba(255,255,255,0.8)",
      }}
    >
      ❄
    </Box>
  );
}

export default function ChristmasDecor() {
  const { i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const [flakes, setFlakes] = useState([]);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!isChristmasPeriod()) return undefined;

    const dismissed = sessionStorage.getItem("ellipse_christmas_banner_dismissed");
    if (!dismissed) setShowBanner(true);

    const spawnFlurry = () => {
      const batch = Array.from({ length: FLAKES_PER_FLURRY }, (_, i) => ({
        id: `${Date.now()}-${i}-${Math.random()}`,
        leftVw: Math.random() * 100,
        duration: 7 + Math.random() * 4,
        size: 12 + Math.random() * 10,
        drift: -40 + Math.random() * 80,
      }));
      setFlakes((f) => [...f, ...batch]);
      setTimeout(() => {
        setFlakes((f) => f.filter((x) => !batch.some((b) => b.id === x.id)));
      }, 12000);
    };

    // Première rafale rapide — sans ça, il faut attendre un intervalle
    // complet (40s) avant de voir le moindre flocon.
    const firstFlurry = setTimeout(spawnFlurry, 2500);
    const flurryTimer = setInterval(spawnFlurry, FLURRY_INTERVAL_MS);

    return () => { clearTimeout(firstFlurry); clearInterval(flurryTimer); };
  }, []);

  if (!isChristmasPeriod()) return null;

  return (
    <>
      <style>{KEYFRAMES}</style>
      {flakes.map((f) => <Snowflake key={f.id} {...f} />)}

      {showBanner && (
        <Box sx={{
          position: "fixed", bottom: 90, right: 20, zIndex: 1290, width: 280,
          bgcolor: "#1a4d3a", borderRadius: 3, p: 2.5, boxShadow: "0 10px 32px rgba(0,0,0,0.3)",
          border: "2px solid #bfd56c",
        }}>
          <Typography sx={{ fontSize: 13, fontWeight: 900, letterSpacing: 1, color: "#bfd56c", textTransform: "uppercase", mb: 0.75 }}>
            🎄 {fr ? "Joyeuses fêtes chez Ellipse" : "Happy Holidays at Ellipse"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#fff", lineHeight: 1.5, mb: 1.5 }}>
            {fr ? "Un peu de magie des fêtes flotte sur l'app jusqu'au 1er janvier." : "A bit of holiday magic around the app until January 1st."}
          </Typography>
          <Button
            size="small"
            onClick={() => { setShowBanner(false); sessionStorage.setItem("ellipse_christmas_banner_dismissed", "1"); }}
            sx={{ bgcolor: "#bfd56c", color: "#0f172a", fontWeight: 700, fontSize: 12, textTransform: "none", "&:hover": { bgcolor: "#a3c60a" } }}
          >
            {fr ? "Fermer" : "Close"}
          </Button>
        </Box>
      )}
    </>
  );
}
